import { useField, FieldHookConfig, useFormikContext } from 'formik';
import { useState, useEffect } from 'react';
import cx from 'classnames';

import PasswordVisibilityToggle from '../PasswordVisibilityToggle';
import styles from './TextInput.module.scss';

interface Props extends React.HTMLProps<HTMLInputElement> {
  fullBorder?: boolean;
  label?: string;
  className?: string;
  required?: boolean;
  validationAttemptCount?: number;
  showPasswordToggle?: boolean;
}

const TextInput = ({
  label,
  fullBorder,
  className,
  required,
  validationAttemptCount,
  showPasswordToggle,
  type,
  ...props
}: Props & FieldHookConfig<string>): JSX.Element => {
  const [field, meta] = useField(props);
  const { submitCount } = useFormikContext();
  const [lastSubmitCount, setLastSubmitCount] = useState(submitCount);
  const [lastValidationAttempt, setLastValidationAttempt] = useState(validationAttemptCount || 0);
  const [shouldShake, setShouldShake] = useState(false);
  // Reveal-password state is intentionally local and not persisted, so the
  // password is always hidden again on the next visit.
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordToggle = Boolean(showPasswordToggle) && type === 'password';
  const effectiveType = isPasswordToggle && showPassword ? 'text' : type;

  const hasError = meta.touched && meta.error;

  // Trigger shake when validation is attempted with error
  useEffect(() => {
    const submitCountIncreased = submitCount > lastSubmitCount;
    const validationAttemptIncreased = validationAttemptCount && validationAttemptCount > lastValidationAttempt;
    const hasErrorNow = meta.touched && meta.error;

    if ((submitCountIncreased || validationAttemptIncreased) && hasErrorNow) {
      setShouldShake(true);
      setLastSubmitCount(submitCount);
      setLastValidationAttempt(validationAttemptCount || 0);

      // Remove shake class after animation completes (500ms)
      const timer = setTimeout(() => {
        setShouldShake(false);
      }, 500);

      return () => clearTimeout(timer);
    } else if (submitCountIncreased) {
      setLastSubmitCount(submitCount);
    } else if (validationAttemptIncreased) {
      setLastValidationAttempt(validationAttemptCount || 0);
    }
  }, [submitCount, validationAttemptCount, meta.error, meta.touched, lastSubmitCount, lastValidationAttempt]);

  return (
    <div className={styles.container}>
      {label && (
        <label
          className={cx(styles.label, {
            [`${styles.labelShake}`]: shouldShake,
          })}
          htmlFor={props.name}
        >
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      <div className={styles.inputWrapper}>
        <input
          {...field}
          {...props}
          type={effectiveType}
          className={cx(styles.input, className, {
            [`${styles.fullBorder}`]: fullBorder,
            [`${styles.error}`]: shouldShake,
            [`${styles.hasError}`]: hasError && !shouldShake,
            [`${styles.withToggle}`]: isPasswordToggle,
          })}
        />
        {isPasswordToggle && (
          <PasswordVisibilityToggle visible={showPassword} onToggle={() => setShowPassword((prev) => !prev)} />
        )}
      </div>
      <div className={styles.errorHint}>{hasError ? meta.error : null}</div>
    </div>
  );
};

export default TextInput;
